// Donations — every receipt in the session's scope (LE + location + department, and the ?cause=
// filter) for one financial year. Rows claiming 80G with no PAN are flagged, and the status
// filter can narrow the list to them. Search, status filter, sort and paging run on the server;
// donor names open the donor menu (Open / Call / WhatsApp / Email).

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock, FileSpreadsheet, Loader2, Search, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { ApiError, saveBlob } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  fetchContextOptions,
  downloadDonationsXlsx,
  fetchDonations,
  formatAmount,
  type BudgetYear,
  type DonationSort,
  type DonationStatus,
  type DonationsPage,
} from "@/lib/donation";
import { formatDate } from "@/lib/format";
import { useSessionScope } from "@/lib/session-scope";
import { PageContainer } from "@/components/page";
import { DatePicker } from "@/components/ui/date-picker";
import { BlockPlaceholder, Busy } from "@/components/busy";
import { CauseFilter, useCauseParam } from "@/components/cause-filter";
import { MobileFilters } from "@/components/filter-sheet";
import { DonorLink } from "@/components/donor-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES: { value: DonationStatus; label: string }[] = [
  { value: "all", label: "All donations" },
  { value: "received", label: "Received only" },
  { value: "pending", label: "Pending only" },
  { value: "80gNoPan", label: "80G without PAN" },
];

const SORTS: { value: DonationSort; label: string }[] = [
  { value: "dateDesc", label: "Newest first" },
  { value: "dateAsc", label: "Oldest first" },
  { value: "amountDesc", label: "Largest amount" },
  { value: "amountAsc", label: "Smallest amount" },
];

export default function Donations() {
  const { token } = useAuth();
  const { scope } = useSessionScope();
  const [params, setParams] = useSearchParams();
  const [causeId] = useCauseParam();

  const search = params.get("q") ?? "";
  const status = (STATUSES.some((s) => s.value === params.get("status")) ? params.get("status") : "all") as DonationStatus;
  const sort = (SORTS.some((s) => s.value === params.get("sort")) ? params.get("sort") : "dateDesc") as DonationSort;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const budgetYearId = Number(params.get("fy")) || null;
  // A date range (either end) replaces the financial year on the server.
  const dateFrom = params.get("from") ?? "";
  const dateTo = params.get("to") ?? "";
  const customRange = !!(dateFrom || dateTo);

  const [query, setQuery] = useState(search);
  const [years, setYears] = useState<BudgetYear[]>([]);
  const [data, setData] = useState<DonationsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  /** Excel export is built on the server, with the same filters as the screen (all rows). */
  async function exportExcel() {
    if (!token || !scope || !data || data.total === 0) return;
    setExporting(true);
    try {
      const { blob, fileName } = await downloadDonationsXlsx(token, scope, {
        budgetYearId,
        causeId,
        search,
        status,
        sort,
        dateFrom,
        dateTo,
      });
      saveBlob(blob, fileName);
    } catch (e) {
      toast.error(`Could not export: ${(e as Error).message}`);
    } finally {
      setExporting(false);
    }
  }

  function update(next: Record<string, string | null>) {
    const q = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v == null || v === "" ? q.delete(k) : q.set(k, v)));
    setParams(q, { replace: true });
  }

  // Typing pauses before the request goes out.
  useEffect(() => {
    const t = setTimeout(() => {
      if (query !== search) update({ q: query, page: null });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchContextOptions(token, controller.signal)
      .then((o) => setYears(o.budgetYears))
      .catch(() => {
        // the year picker just stays empty; the list still uses the default FY
      });
    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!token || !scope) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDonations(
      token,
      scope,
      { budgetYearId, causeId, search, status, sort, page, dateFrom, dateTo },
      controller.signal
    )
      .then(setData)
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setError(
          e instanceof ApiError && e.status === 403
            ? "You do not have access to donations here."
            : (e as Error).message
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, scope, budgetYearId, causeId, search, status, sort, page, dateFrom, dateTo]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const fyItems = years.map((y) => ({ value: String(y.id), label: y.name }));
  // Rendered inline on desktop and inside the phone filter sheet — one definition, two places.
  const filterControls = (
    <>
      <div className="w-full md:w-40">
        <Select
          items={fyItems}
          value={data ? String(data.budgetYear.id) : null}
          onValueChange={(v) => v && update({ fy: String(v), page: null })}
          disabled={!fyItems.length || customRange}
        >
          <SelectTrigger className="w-full" aria-label="Financial year">
            <SelectValue placeholder="Financial year" />
          </SelectTrigger>
          <SelectContent>
            {fyItems.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Stacked on a phone (inside the filter sheet), side by side from md up. */}
      <div className="flex w-full flex-col gap-1 md:w-auto md:flex-row md:items-center">
        <DatePicker
          value={dateFrom}
          onChange={(v) => update({ from: v, page: null })}
          placeholder="From date"
          className="w-full md:w-36"
        />
        <span className="hidden text-xs text-muted-foreground md:inline">to</span>
        <DatePicker
          value={dateTo}
          onChange={(v) => update({ to: v, page: null })}
          placeholder="To date"
          className="w-full md:w-36"
        />
        {customRange && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => update({ from: null, to: null, page: null })}
            title="Clear the date range and use the financial year"
          >
            Clear
          </Button>
        )}
      </div>
      <div className="w-full md:w-44">
        <Select items={STATUSES} value={status} onValueChange={(v) => v && update({ status: String(v), page: null })}>
          <SelectTrigger className="w-full" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-full md:w-44">
        <Select items={SORTS} value={sort} onValueChange={(v) => v && update({ sort: String(v), page: null })}>
          <SelectTrigger className="w-full" aria-label="Sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <CauseFilter className="w-full md:w-56" />
    </>
  );
  const activeFilterCount =
    (status !== "all" ? 1 : 0) + (sort !== "dateDesc" ? 1 : 0) + (causeId ? 1 : 0) + (customRange ? 1 : 0);

  return (
    <PageContainer full fill>
      {/* The page fills the screen and only the table scrolls, so the filters, the column
          header and the pager stay put. On a phone the filters live behind a button. */}
      <div className="flex shrink-0 flex-col gap-3">
        <div className="flex items-end gap-3">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search donor, name on receipt or receipt number…"
              className="pl-8"
            />
          </div>
          <div className="hidden flex-wrap items-end gap-3 md:flex">{filterControls}</div>
          <div className="md:hidden">
            <MobileFilters activeCount={activeFilterCount}>{filterControls}</MobileFilters>
          </div>
          <Button
            variant="outline"
            onClick={exportExcel}
            disabled={!data || data.total === 0 || exporting}
            className="shrink-0"
          >
            {exporting ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />}
            <span className="hidden sm:inline">Export to Excel</span>
          </Button>
        </div>

      <p className="text-sm text-muted-foreground">
          {scope?.operationUnit.name}
          {scope?.department ? ` · ${scope.department.name}` : ""} · {scope?.legalEntity.name}
          {data && (
            <>
              {" · "}
              {data.customRange
                ? `${formatDate(data.dateFrom)} – ${formatDate(data.dateTo)}`
                : `FY ${data.budgetYear.name}`}
              {" · "}
              {data.total.toLocaleString("en-IN")} {data.total === 1 ? "donation" : "donations"} ·{" "}
              {formatAmount(data.totalAmount)}
              {data.total > data.pageSize ? ` · page ${data.page} of ${totalPages}` : ""}
            </>
          )}
        </p>
      </div>

      {error ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">{error}</CardContent>
        </Card>
      ) : !data ? (
        <BlockPlaceholder className="h-96" />
      ) : (
        <Busy busy={loading} fill className="flex min-h-0 flex-1 flex-col">
          <Card className="flex min-h-0 flex-1 flex-col py-0">
            <CardContent className="min-h-0 flex-1 p-0">
              {data.rows.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">
                  {search ? "No donation matches this search." : "No donations in this period."}
                </p>
              ) : (
                <div className="h-full overflow-auto">
                  <table className="w-full text-sm">
                    {/* Opaque background: rows scroll underneath this row. */}
                    <thead className="sticky top-0 z-10 bg-muted">
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Receipt</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Date</th>
                        <th className="px-3 py-2 font-medium">Donor</th>
                        <th className="px-3 py-2 font-medium">Causes</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Mode</th>
                        <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <tr key={r.donationId} className="border-b border-border/60 last:border-0">
                          <td className="px-3 py-2 whitespace-nowrap">
                            {r.receiptNumber ?? "—"}
                            {!r.received && (
                              <Badge variant="outline" className="ml-2 gap-1 text-[10px]">
                                <Clock className="size-3" /> Pending
                              </Badge>
                            )}
                            {r.panMissing && (
                              <Badge
                                variant="outline"
                                className="ml-2 gap-1 border-amber-300 bg-amber-50 text-[10px] text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
                                title="80G claimed but no PAN on this receipt"
                              >
                                <TriangleAlert className="size-3" /> 80G · no PAN
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.date)}</td>
                          <td className="px-3 py-2">
                            <DonorLink
                              name={r.name || "(no name)"}
                              donorId={r.donorId}
                            />
                          </td>
                          <td className="px-3 py-2">{r.causes ?? "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{r.modeOfPayment ?? "—"}</td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                            {formatAmount(r.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </Busy>
      )}

      {data && totalPages > 1 && (
        <div className="flex shrink-0 items-center justify-center gap-2 pb-1">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
            <ChevronLeft /> Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => update({ page: String(page + 1) })}
          >
            Next <ChevronRight />
          </Button>
        </div>
      )}
    </PageContainer>
  );
}
